import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { formatDate } from '../utils/payrollUtils';

const AttendanceCard = ({ employeeNumber, employeeIdKf, employeeIdKoo }) => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [payrollPeriods, setPayrollPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedPeriod, setExpandedPeriod] = useState(null);

  useEffect(() => {
    fetchAttendanceRecords();
  }, [employeeNumber, employeeIdKf, employeeIdKoo]);

  const fetchAttendanceRecords = async () => {
    try {
      setLoading(true);
      setError('');

      // First, get the current and recent payroll periods for attendance (show latest regardless of published status)
      const { data: periodsData, error: periodsError } = await supabase
        .from('payroll_period')
        .select('*')
        .lte('end_date', new Date().toISOString().split('T')[0]) // Only periods that have ended
        .gte('payroll_number', 2513) // Only show period 2513 (June 9-22, 2025) and later
        .order('payroll_number', { ascending: false })
        .limit(3);

      if (periodsError) {
        console.warn('Error fetching payroll periods:', periodsError);
        // Fallback to hardcoded periods
        setPayrollPeriods([
          { payroll_number: 2514, start_date: '2025-06-23', end_date: '2025-07-06', period_label: 'June 23 - July 6, 2025' },
          { payroll_number: 2513, start_date: '2025-06-09', end_date: '2025-06-22', period_label: 'June 9-22, 2025' }
        ]);
      } else {
        setPayrollPeriods(periodsData || []);
      }

      const currentPeriods = periodsData || [
        { payroll_number: 2514 },
        { payroll_number: 2513 }
      ];

      // Fetch payroll records for summary totals (from all periods including unpublished)
      if (employeeNumber || employeeIdKf || employeeIdKoo) {
        // Get all periods to show payroll data even for unpublished periods
        if (currentPeriods.length > 0) {
          const payrollNumbers = currentPeriods.map(p => p.payroll_number);
          let payrollQuery = supabase
            .from('payroll_summary')
            .select('*')
            .in('payroll_number', payrollNumbers)
            .order('payroll_number', { ascending: false });

          // Apply case-insensitive employee matching
          if (employeeNumber) {
            payrollQuery = payrollQuery.ilike('employee_number', employeeNumber);
          } else {
            // Fallback to employee IDs if no employee_number
            const payrollConditions = [];
            if (employeeIdKf) payrollConditions.push(`employee_id_kf.eq.${employeeIdKf}`);
            if (employeeIdKoo) payrollConditions.push(`employee_id_koo.eq.${employeeIdKoo}`);
            
            if (payrollConditions.length > 0) {
              payrollQuery = payrollQuery.or(payrollConditions.join(','));
            }
          }

          const { data: payrollData, error: payrollError } = await payrollQuery;

          if (payrollError) {
            console.warn('Error fetching payroll records:', payrollError);
          } else {
            setPayrollRecords(payrollData || []);
          }
        }
      }

      // Fetch attendance records for daily details
      // Try employee_number first as it's the most reliable
      let attendanceQuery = supabase
        .from('payroll_attendance')
        .select('*')
        .order('date', { ascending: false });

      if (employeeNumber) {
        attendanceQuery = attendanceQuery.ilike('employee_number', employeeNumber);
      } else {
        // Fallback to employee IDs if no employee_number
        const attendanceConditions = [];
        if (employeeIdKf) attendanceConditions.push(`employee_id_kf.eq.${employeeIdKf}`);
        if (employeeIdKoo) attendanceConditions.push(`employee_id_koo.eq.${employeeIdKoo}`);
        
        if (attendanceConditions.length > 0) {
          attendanceQuery = attendanceQuery.or(attendanceConditions.join(','));
        }
      }

      // Filter for the payroll periods - get all records for all relevant periods
      if (currentPeriods.length > 0) {
        // Get the earliest start date and latest end date to capture all periods
        const startDate = currentPeriods[currentPeriods.length - 1].start_date || '2025-06-09';
        const endDate = currentPeriods[0].end_date || '2025-07-06';
        attendanceQuery = attendanceQuery.gte('date', startDate).lte('date', endDate);
        
      }

      const { data: attendanceData, error: attendanceError } = await attendanceQuery;

      if (attendanceError) {
        console.warn('Error fetching attendance records:', attendanceError);
      } else {
        // Debug: Show what employee identifiers are being used and date range
        if (currentPeriods.length > 0) {
          const startDate = currentPeriods[currentPeriods.length - 1].start_date || '2025-06-09';
          const endDate = currentPeriods[0].end_date || '2025-07-06';
          console.log('Attendance query debug:', {
            employeeNumber,
            employeeIdKf,
            employeeIdKoo,
            dateRange: `${startDate} to ${endDate}`,
            recordCount: attendanceData?.length || 0,
            records: attendanceData?.map(r => ({ employee_number: r.employee_number, date: r.date, hours: r.hours })) || []
          });
        }
        setAttendanceRecords(attendanceData || []);
      }

    } catch (error) {
      console.error('Error fetching records:', error);
      setError(`Failed to load records: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Group attendance records by payroll period (for daily details)
  const groupedAttendance = payrollPeriods.reduce((groups, period) => {
    const periodRecords = attendanceRecords.filter(record => {
      const recordDate = new Date(record.date);
      const startDate = new Date(period.start_date);
      const endDate = new Date(period.end_date);
      const matches = recordDate >= startDate && recordDate <= endDate;
      return matches;
    });


    groups[period.period_label] = periodRecords;
    return groups;
  }, {});

  // Group payroll records by payroll period (for summary totals)
  const groupedPayroll = payrollPeriods.reduce((groups, period) => {
    const periodRecords = payrollRecords.filter(record => {
      return record.payroll_number === period.payroll_number;
    });

    groups[period.period_label] = periodRecords;
    return groups;
  }, {});

  // Calculate totals from payroll verification table
  const calculatePayrollTotals = (records) => {
    const totals = records.reduce((sum, record) => {
      return {
        totalHours: sum.totalHours + parseFloat(record.total_hours || 0),
        regularHours: sum.regularHours + parseFloat(record.check_hours || 0),
        weekdayHours: sum.weekdayHours + parseFloat(record.weekday_cash_hours || 0),
        weekendHours: sum.weekendHours + parseFloat(record.weekend_cash_hours || 0),
        ptoHours: sum.ptoHours + parseFloat(record.pto_hp || 0),
        sickHours: sum.sickHours + parseFloat(record.sick_leave || 0),
        lunchHours: sum.lunchHours + parseFloat(record.lunch || 0),
        recordCount: sum.recordCount + 1
      };
    }, {
      totalHours: 0,
      regularHours: 0,
      weekdayHours: 0,
      weekendHours: 0,
      ptoHours: 0,
      sickHours: 0,
      lunchHours: 0,
      recordCount: 0
    });

    return totals;
  };

  // Calculate totals from attendance table (for details)
  const calculateAttendanceTotals = (records) => {
    const totals = records.reduce((sum, record) => {
      return {
        totalHours: sum.totalHours + parseFloat(record.hours || 0),
        regularHours: sum.regularHours + parseFloat(record.hours || 0), // Use hours field for regular
        overtimeHours: sum.overtimeHours + 0, // No overtime field in payroll_attendance
        ptoHours: sum.ptoHours + 0, // No PTO field in payroll_attendance
        sickHours: sum.sickHours + 0, // No sick field in payroll_attendance
        lunchHours: sum.lunchHours + 0, // No lunch field in payroll_attendance
        daysWorked: sum.daysWorked + (parseFloat(record.hours || 0) > 0 ? 1 : 0)
      };
    }, {
      totalHours: 0,
      regularHours: 0,
      overtimeHours: 0,
      ptoHours: 0,
      sickHours: 0,
      lunchHours: 0,
      daysWorked: 0
    });

    return totals;
  };

  // Get store code mapping
  const getStoreCode = (storeName) => {
    if (!storeName) return '';
    
    const normalizedName = storeName.trim();
    
    const storeCodes = {
      'Main St': 'M',
      'Main Street': 'M',
      'Kissena Blvd': 'K',
      'Kissena': 'K',
      '39th Ave': '39TH',
      '39TH': '39TH',
      'Roosevelt Ave': 'R',
      'Roosevelt': 'R',
      'Albany': 'AL',
      'Arbutus': 'ARB',
      'Columbia': 'CM',
      'Ellicott City': 'EC',
      'Essex': 'EX',
      'Frederick': 'FK',
      'Glen Burnie': 'GL',
      'Highland': 'HL',
      'Laurel': 'LR',
      'Odenton': 'OD',
      'Owings Mills': 'OW',
      'Pikesville': 'PV',
      'Rockville Plaza': 'RP',
      'Rio': 'RR',
      'Silver Spring': 'SC',
      'Severna Park': 'SV',
      'Towson': 'TW',
      'White Marsh': 'WH',
      'Falls Church': 'FLS',
      'Germantown Commons': 'GTC'
    };
    
    if (storeCodes[normalizedName]) {
      return storeCodes[normalizedName];
    }
    
    const lowerName = normalizedName.toLowerCase();
    for (const [storeName, code] of Object.entries(storeCodes)) {
      if (lowerName.includes(storeName.toLowerCase()) || storeName.toLowerCase().includes(lowerName)) {
        return code;
      }
    }
    
    return normalizedName.length > 4 ? normalizedName.substring(0, 4).toUpperCase() : normalizedName.toUpperCase();
  };

  // Generate calendar data for a period
  const generateCalendarData = (period, records) => {
    if (!period || !period.start_date || !period.end_date) return [];
    
    const calendarData = [];
    const weekDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    
    const startDate = new Date(period.start_date + 'T00:00:00');
    const endDate = new Date(period.end_date + 'T00:00:00');
    
    const periodDates = [];
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      periodDates.push(new Date(d).toISOString().split('T')[0]);
    }
    
    const dateToCalendarPosition = {};
    periodDates.forEach((date, index) => {
      const week = Math.floor(index / 7) + 1;
      const dayIndex = index % 7;
      const dayName = weekDays[dayIndex];
      const position = `week${week}_${dayName}`;
      dateToCalendarPosition[date] = position;
    });
    
    for (let week = 1; week <= 2; week++) {
      const weekData = [];
      for (const day of weekDays) {
        const fieldName = `week${week}_${day}`;
        
        const actualDate = Object.keys(dateToCalendarPosition).find(
          date => dateToCalendarPosition[date] === fieldName
        );
        
        const attendanceInfo = actualDate ? records.find(item => item.date === actualDate) : null;
        const hours = attendanceInfo ? parseFloat(attendanceInfo.hours || 0) : 0;
        const storeCode = getStoreCode(attendanceInfo?.store_name);
        
        weekData.push({
          day: day.charAt(0).toUpperCase() + day.slice(1),
          hours: hours,
          store: storeCode,
          storeName: attendanceInfo?.store_name || '',
          date: actualDate || '',
          company: attendanceInfo?.company || ''
        });
      }
      calendarData.push(weekData);
    }
    
    return calendarData;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-none sm:rounded-lg shadow-none sm:shadow-lg p-4 w-full max-w-6xl mx-auto">
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">Loading attendance records...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-none sm:rounded-lg shadow-none sm:shadow-lg p-4 w-full max-w-6xl mx-auto">
        <div className="flex items-center justify-center py-8">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-none sm:rounded-lg shadow-none sm:shadow-lg p-2 sm:p-6 w-full max-w-6xl mx-auto">
      <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-6">Your Attendance</h2>
      
      <div className="space-y-4 sm:space-y-6">
        {payrollPeriods.map((period) => {
          const attendanceRecordsForPeriod = groupedAttendance[period.period_label] || [];
          const payrollRecordsForPeriod = groupedPayroll[period.period_label] || [];
          const payrollTotals = calculatePayrollTotals(payrollRecordsForPeriod);
          const attendanceTotals = calculateAttendanceTotals(attendanceRecordsForPeriod);
          const isExpanded = expandedPeriod === period.period_label;

          return (
            <div key={period.period_label} className="border border-gray-300 rounded-none sm:rounded-lg overflow-hidden">
              {/* Period Summary - Using Payroll Data */}
              <div className={`bg-gradient-to-r ${period.status === 'Published' ? 'from-green-50 to-green-100' : 'from-yellow-50 to-yellow-100'} p-3 sm:p-4 border-b border-gray-300`}>
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
                  <div className="flex-1">
                    <h3 className="text-base sm:text-xl font-bold text-gray-800">
                      {period.period_label}
                      {period.status !== 'Published' && (
                        <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded">
                          Attendance Only
                        </span>
                      )}
                    </h3>
                    <p className="text-xs sm:text-base text-gray-600">
                      {attendanceTotals.daysWorked} days worked • {payrollTotals.recordCount} payroll record{payrollTotals.recordCount !== 1 ? 's' : ''}
                      {period.status !== 'Published' && payrollTotals.recordCount === 0 && (
                        <span className="text-yellow-700"> • Payroll not yet published</span>
                      )}
                    </p>
                  </div>
                  <div className="flex-1">
                  </div>
                </div>
              </div>

              {/* Summary View with View Details Button */}
              {!isExpanded ? (
                <div className="p-3 sm:p-4">
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Daily Records:</span>
                          <span className="font-semibold text-gray-800">{attendanceRecordsForPeriod.length}</span>
                        </div>
                        <div className="flex justify-between items-center border-b pb-2">
                          <span className="text-gray-600">Total Hours:</span>
                          <span className="font-bold text-xl text-gray-800">{payrollTotals.totalHours.toFixed(2)}h</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Timesheet Hours:</span>
                            <span className="font-medium text-blue-600">{attendanceTotals.regularHours.toFixed(2)}h</span>
                          </div>
                          {payrollTotals.ptoHours > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">PTO/Holiday:</span>
                              <span className="font-medium text-purple-600">{payrollTotals.ptoHours.toFixed(2)}h</span>
                            </div>
                          )}
                          {payrollTotals.sickHours > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Sick Leave:</span>
                              <span className="font-medium text-red-600">{payrollTotals.sickHours.toFixed(2)}h</span>
                            </div>
                          )}
                          {payrollTotals.lunchHours > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Lunch:</span>
                              <span className="font-medium text-green-600">{payrollTotals.lunchHours.toFixed(2)}h</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => setExpandedPeriod(period.period_label)}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                        View Details
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Biweekly Timesheet Calendar */}
                  <div className="p-3 sm:p-4 bg-gray-50">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm sm:text-lg font-bold text-gray-800">Biweekly Timesheet Calendar</h3>
                      <button
                        onClick={() => setExpandedPeriod(null)}
                        className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                        Collapse
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      Pay Period: {period.period_label} | Store Codes: M=Main St, K=Kissena, 39TH=39th Ave, R=Roosevelt
                    </p>
                    {(() => {
                      const calendarData = generateCalendarData(period, attendanceRecordsForPeriod);
                      return calendarData.map((week, weekIndex) => (
                        <div key={weekIndex} className="mb-4">
                          <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Week {weekIndex + 1}</h4>
                          <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
                            {week.map((day, dayIndex) => (
                              <div 
                                key={dayIndex} 
                                className={`p-1 sm:p-2 rounded text-center transition-all ${
                                  day.hours > 0 ? 'bg-blue-100 border border-blue-300' : 'bg-gray-100'
                                }`}
                                title={day.storeName ? `${day.storeName} - ${day.date}` : day.date}
                              >
                                <div className="text-xs font-medium text-gray-500 mb-0.5">
                                  {day.date ? day.date.split('-')[2] : ''}
                                </div>
                                <div className="font-semibold text-xs text-gray-700">{day.day}</div>
                                <div className="text-sm font-bold text-gray-900 my-0.5">{day.hours || '-'}</div>
                                {day.store && (
                                  <div className="text-xs font-medium text-blue-700 bg-blue-200 rounded px-0.5 py-0.5">
                                    {day.store}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AttendanceCard;